import React, { useCallback, useEffect, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import cx from 'clsx'
import { LOCATION_SEARCH_ERROR_MESSAGE } from 'constants/search'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import ListItemLoadingSkeleton from 'components/Luxkit/List/ListItemLoadingSkeleton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'

import CruiseDepartureSelectListAll from './CruiseDepartureSelectListAll'
import CruiseDepartureSelectListItem from './CruiseDepartureSelectListItem'
import CruiseDepartureSelectListByRegion from './CruiseDepartureSelectListByRegion'
import { DeparturePortMapped, DepartureTabRegion } from './CruiseDepartureSelect'

const CruiseDepartureSelectWrapper = styled.div`
    align-items: center;
    flex-wrap: wrap;
    overflow-y: auto;
    justify-content: center;
    display: grid;
    row-gap: ${rem(12)};
    column-gap: ${rem(40)};
    grid-template-columns: repeat(1, 1fr);

    ${mediaQueryUp.tablet} {
        grid-template-columns: repeat(2, 1fr);
        justify-content: flex-start;
        row-gap: ${rem(12)};
        column-gap: ${rem(40)};
        max-height: ${rem(340)};
    }

    ${mediaQueryUp.desktop} {
        grid-template-columns: repeat(3, 1fr);
    }

    &.drawer {
        grid-template-columns: repeat(1, 1fr);
        max-height: unset;
    }
`

const LayoutPadding = styled.div`
    padding-top: ${rem(20)};


    ${mediaQueryDown.mobile} {
        padding: ${rem(20)};
    }
`

interface Props {
  searchDeparturePorts: DeparturePortMapped[]
  searchTerm: string;
  secondarySearchItems: App.SearchItem[];
  isMobileMode?: boolean;
  isAllSelected: boolean;
  selectedRegions: string[];
  onChange: (values: App.SearchItem[]) => void;
  inputSearchRef?: React.RefObject<HTMLInputElement>;
  selectedRegion: DepartureTabRegion;
  setIsAllSelected: (value: boolean) => void;
  setSelectedRegions: (value: string[]) => void;
  setSelectedRegion: (value: DepartureTabRegion) => void;
  fetching: boolean;
  regions: DepartureTabRegion[];
}

function CruiseDepartureSelectList({
  searchDeparturePorts,
  searchTerm,
  secondarySearchItems,
  isMobileMode,
  isAllSelected,
  selectedRegions,
  onChange,
  inputSearchRef,
  selectedRegion,
  setIsAllSelected,
  setSelectedRegions,
  fetching,
  regions,
}: Props) {
  const isAllDisabled = useMemo(() => {
    return searchDeparturePorts.every?.(({ isDisabled }) => isDisabled)
  }, [searchDeparturePorts])

  useEffect(() => {
    if (inputSearchRef?.current) {
      inputSearchRef.current.focus({ preventScroll: true })
    }
  }, [inputSearchRef])

  const markRegionByUrlParams = useCallback(() => {
    if (secondarySearchItems.length) {
      const identifiers = secondarySearchItems
        .filter(({ searchType }) => searchType === 'destination')
        .map(({ value }) => {
          const region = regions.find(({ placeIds }) => placeIds.includes(value))
          return region?.identifier!
        })

      if (identifiers.length) {
        setSelectedRegions([...selectedRegions, ...identifiers])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    markRegionByUrlParams()
  }, [markRegionByUrlParams, fetching])

  const handleSelectByRegion = useCallback(() => {
    setIsAllSelected(false)
    setSelectedRegions([...selectedRegions, selectedRegion.identifier])
    onChange([
      ...secondarySearchItems, {
        searchType: 'departurePort',
        value: selectedRegion.placeIds,
        format: {
          mainText: selectedRegion.name,
        },
      },
    ])
  }, [onChange, secondarySearchItems, selectedRegion, selectedRegions, setIsAllSelected, setSelectedRegions])

  const handleUnselectByRegion = useCallback(() => {
    if (isAllSelected) {
      setIsAllSelected(false)
      setSelectedRegions([selectedRegion.identifier])
      onChange([{
        searchType: 'departurePort',
        value: selectedRegion.placeIds,
        format: {
          mainText: selectedRegion.name,
        },
      }])
    } else {
      setSelectedRegions(selectedRegions.filter((region) => region !== selectedRegion.identifier))
      const newSearchItems = secondarySearchItems.filter(({ value }) => value !== selectedRegion.placeIds)
      onChange(newSearchItems)
    }
  }, [isAllSelected, onChange, secondarySearchItems, selectedRegion.identifier, selectedRegion.name, selectedRegion.placeIds, selectedRegions, setIsAllSelected, setSelectedRegions])

  const handleSelectItem = useCallback((item: App.SearchItem) => {
    const newSearchItems = [...secondarySearchItems, item]
    onChange(newSearchItems)
  }, [onChange, secondarySearchItems])

  const handleUnselectItem = useCallback((item: App.SearchItem) => {
    const allTabRegionSelected = selectedRegions.includes(selectedRegion.identifier)
    // When is all selected and click on a single item, only keep selected the clicked item
    if (isAllSelected) {
      onChange([item])
      setSelectedRegions([])
    }
    // When all tab region is selected and click on a single item, only keep selected the clicked item and the items from the other tabs
    else if (allTabRegionSelected) {
      setSelectedRegions(selectedRegions.filter((region) => region !== selectedRegion.identifier))
      onChange([
        ...secondarySearchItems.filter(({ value }) => (
          !searchDeparturePorts.some?.(({ searchItem }) => searchItem.value === value)
        )),
        item,
      ])
    } else {
      onChange(secondarySearchItems.filter(({ value }) => value !== item.value))
    }
  }, [isAllSelected, onChange, searchDeparturePorts, secondarySearchItems, selectedRegion.identifier, selectedRegions, setSelectedRegions])

  const handleSelectAll = useCallback(() => {
    onChange([])
    setIsAllSelected(!isAllSelected)
    setSelectedRegions([])
  }, [isAllSelected, onChange, setIsAllSelected, setSelectedRegions])

  return <VerticalSpacer gap={16}>
    {fetching && <>
      <ListItemLoadingSkeleton subtitle/>
      <ListItemLoadingSkeleton subtitle/>
      <ListItemLoadingSkeleton subtitle/>
    </>}

    {!fetching && <CruiseDepartureSelectWrapper data-testid="cruise-departure-select-list-wrapper"
                                                className={cx({ drawer: isMobileMode })}>
      {!isMobileMode && !searchTerm && <CruiseDepartureSelectListAll
        isSelected={isAllSelected}
        onClick={handleSelectAll}
      />}

      {(!searchTerm || !searchDeparturePorts.length) && <CruiseDepartureSelectListByRegion
        isSelected={selectedRegions.includes(selectedRegion.identifier) || isAllSelected}
        onSelect={handleSelectByRegion}
        onUnselect={handleUnselectByRegion}
        departurePort={selectedRegion}
        isDisabled={isAllDisabled}
      />}

      {searchDeparturePorts.map(({
        searchItem,
        isSelected,
        isDisabled,
      }) => <CruiseDepartureSelectListItem
        key={searchItem.value}
        searchItem={searchItem}
        onSelect={handleSelectItem}
        onUnselect={handleUnselectItem}
        isSelected={isSelected || selectedRegions.includes(selectedRegion.identifier)}
        isDisabled={isDisabled}
        isSearching={!!searchTerm}
        searchTerm={searchTerm}
      />)}

      {!searchDeparturePorts.length && <LayoutPadding>
        <BodyText variant="medium" colour="neutral-two">
          Sorry, we couldn't find <b>{searchTerm}</b>.
        </BodyText>
        <Caption variant="medium">{LOCATION_SEARCH_ERROR_MESSAGE}</Caption>
      </LayoutPadding>}

    </CruiseDepartureSelectWrapper>}
  </VerticalSpacer>
}

export default CruiseDepartureSelectList
